import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { conflictKey } from '@/lib/crm/components/associations/conflict-key';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { CardinalityConflict } from '@/lib/crm/core/types';
import { useAssociationConflicts } from '@/lib/crm/hooks/useAssociationConflicts';

export interface UseCreateConflictsParams {
  orgId: number;
  entityType: string;
  formData: Record<string, unknown>;
  filteredAssociationTypes: AssociationType[];
  draftIds: Set<string>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}

export interface UseCreateConflictsResult {
  conflicts: CardinalityConflict[];
  conflictsError: Error | null;
  ackedEntityIds: Set<string>;
  blockedByConflicts: boolean;
  conflictPanelRef: React.RefObject<HTMLDivElement | null>;
  handleAcknowledgeConflict: (
    entityId: string,
    associationTypeName: string,
    checked: boolean
  ) => void;
  handleRemoveConflictTarget: (
    entityId: string,
    associationTypeName: string
  ) => void;
  /** Clears every acknowledgement — used by "create & add another" resets. */
  resetAcked: () => void;
}

/**
 * Pre-submit association-conflict detection and acknowledgement for the CRM
 * create form. Builds the association inputs to validate, drives
 * `useAssociationConflicts`, and owns the per-conflict acknowledgement set
 * the conflict resolution panel reads/writes.
 */
export function useCreateConflicts({
  orgId,
  entityType,
  formData,
  filteredAssociationTypes,
  draftIds,
  setFormData,
}: UseCreateConflictsParams): UseCreateConflictsResult {
  // Stable placeholder entity ID for conflict pre-validation on create.
  // A new entity doesn't exist yet; this ID is only used as the "source" for
  // the validate endpoint which checks targets, not the source. Must be a valid
  // UUID — the backend serializer rejects non-UUID entity_id values.
  const [placeholderEntityId] = useState(() => crypto.randomUUID());

  // Build association inputs from current form values for conflict pre-validation.
  // Only non-empty association fields are included; the hook skips validation
  // when the resulting array is empty (sortedKey === ''). Draft ids are
  // excluded — they don't exist server-side yet, so the conflict-check
  // endpoint has nothing to validate against.
  const conflictAssociations = useMemo(() => {
    const result: Array<{
      association_type_name: string;
      target_entity_id: string;
    }> = [];
    filteredAssociationTypes.forEach(assoc => {
      const val = formData[assoc.association_type_name];
      if (!val) {
        return;
      }
      const ids = Array.isArray(val) ? val : [val as string];
      ids.forEach(id => {
        if (id && !draftIds.has(id)) {
          result.push({
            association_type_name: assoc.association_type_name,
            target_entity_id: id,
          });
        }
      });
    });
    return result;
  }, [formData, filteredAssociationTypes, draftIds]);

  const { conflicts, error: conflictsError } = useAssociationConflicts({
    orgId,
    sourceEntityType: entityType,
    sourceEntityId: placeholderEntityId,
    associations: conflictAssociations,
  });

  // Per-conflict acknowledgment set: entity_ids the user has checked for overwrite.
  const [ackedEntityIds, setAckedEntityIds] = useState<Set<string>>(
    () => new Set()
  );

  // Prune ack keys whose (entity_id, association_type_name) pair is no longer
  // a pending addition. Without this, removing or replacing an association
  // value via the picker leaves a stale ack behind that would leak into
  // overwrite_targets on submit.
  useEffect(() => {
    setAckedEntityIds(prev => {
      if (prev.size === 0) {
        return prev;
      }
      const pendingKeys = new Set(
        conflictAssociations.map(a =>
          conflictKey(a.target_entity_id, a.association_type_name)
        )
      );
      let mutated = false;
      const next = new Set<string>();
      for (const key of prev) {
        if (pendingKeys.has(key)) {
          next.add(key);
        } else {
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [conflictAssociations]);

  // Gate: blocked when known unresolved conflicts or a validation error exist.
  // Does NOT block while the initial async check is running (conflicts.length === 0).
  const allConflictsAcked =
    conflicts.length === 0 ||
    conflicts.every(c =>
      ackedEntityIds.has(
        conflictKey(c.entity_id, c.association_type_name ?? '')
      )
    );
  const blockedByConflicts =
    conflictAssociations.length > 0 &&
    (conflictsError !== null || (conflicts.length > 0 && !allConflictsAcked));

  // Ref for moving focus to the first unresolved conflict on a blocked submit attempt.
  const conflictPanelRef = useRef<HTMLDivElement>(null);

  const handleAcknowledgeConflict = useCallback(
    (entityId: string, associationTypeName: string, checked: boolean) => {
      setAckedEntityIds(prev => {
        const next = new Set(prev);
        const key = conflictKey(entityId, associationTypeName);
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    []
  );

  const handleRemoveConflictTarget = useCallback(
    (entityId: string, associationTypeName: string) => {
      setFormData(prev => {
        const updated = { ...prev };
        filteredAssociationTypes.forEach(assoc => {
          if (assoc.association_type_name !== associationTypeName) {
            return;
          }
          const val = prev[assoc.association_type_name];
          if (Array.isArray(val) && (val as string[]).includes(entityId)) {
            updated[assoc.association_type_name] = (val as string[]).filter(
              id => id !== entityId
            );
          } else if (val === entityId) {
            updated[assoc.association_type_name] = null;
          }
        });
        return updated;
      });
      setAckedEntityIds(prev => {
        const next = new Set(prev);
        next.delete(conflictKey(entityId, associationTypeName));
        return next;
      });
    },
    [filteredAssociationTypes, setFormData]
  );

  const resetAcked = useCallback(() => {
    setAckedEntityIds(new Set());
  }, []);

  return {
    conflicts,
    conflictsError,
    ackedEntityIds,
    blockedByConflicts,
    conflictPanelRef,
    handleAcknowledgeConflict,
    handleRemoveConflictTarget,
    resetAcked,
  };
}
